
interface Coach {
  id: string;
  name: string;
  specialty: string;
  experience: number;
  price: number;
  location: string;
  rating: number;
  reviews: number;
  languages: string[];
  description: string;
  avatar?: string;
}

interface CoachCardProps {
  coach: Coach;
  onBook: () => void;
}

export default function CoachCard({ coach, onBook }: CoachCardProps) {
  const handleViewProfile = () => {
    console.log(`Voir profil du coach ${coach.id}`);
  };

  // Valeurs par défaut pour éviter les erreurs
  const languages = coach.languages || [];
  const certifications = ['Certification Professionnelle']; // Valeur par défaut
  const responseTime = '2h'; // Valeur par défaut
  const availability = 'Disponible'; // Valeur par défaut
  const isOnline = true; // Valeur par défaut

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="relative">
        <img 
          src={coach.avatar || `https://public.readdy.ai/ai/img_res/a8e0e2d4ba094fad3d39ec20fc8b368c.jpg${coach.id}&orientation=squarish`} 
          alt={coach.name}
          className="w-full h-64 object-cover object-top"
        />
        <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-bold text-blue-600 shadow-md">
          {coach.price}€/h
        </div>
        <div className="absolute top-4 left-4 flex gap-2">
          {isOnline && (
            <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
              En ligne
            </span>
          )}
          <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">
            {availability}
          </span>
        </div>
      </div>
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">{coach.name}</h3>
            <p className="text-blue-600 font-semibold text-sm">{coach.specialty}</p>
          </div>
          <div className="flex items-center bg-yellow-50 px-2 py-1 rounded-lg">
            <i className="ri-star-fill text-yellow-400 mr-1 text-sm"></i>
            <span className="text-sm font-bold text-gray-800">{coach.rating}</span>
            <span className="text-xs text-gray-500 ml-1">({coach.reviews})</span>
          </div>
        </div>

        <div className="flex items-center text-gray-600 text-sm mb-2">
          <i className="ri-map-pin-line mr-2 text-blue-500"></i>
          <span>{coach.location}</span>
        </div>
        
        <div className="flex items-center text-gray-600 text-sm mb-3">
          <i className="ri-time-line mr-2 text-green-500"></i>
          <span>{coach.experience} ans d'expérience • Répond en {responseTime}</span>
        </div>
        
        <p className="text-gray-700 mb-4 text-sm leading-relaxed line-clamp-2">{coach.description}</p>
        
        {/* Languages */}
        <div className="flex flex-wrap gap-1 mb-3">
          {languages.map((lang, index) => (
            <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
              {lang}
            </span>
          ))}
        </div>

        {/* Certifications */}
        {certifications.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center text-xs text-gray-600 mb-1">
              <i className="ri-award-line mr-1"></i>
              <span>Certifications:</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {certifications.slice(0, 2).map((cert, index) => (
                <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                  {cert}
                </span>
              ))}
              {certifications.length > 2 && (
                <span className="text-xs text-gray-500">+{certifications.length - 2}</span>
              )}
            </div>
          </div>
        )}
        
        <div className="flex gap-2">
          <button 
            onClick={handleViewProfile}
            className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
          >
            Voir profil
          </button>
          <button 
            onClick={onBook}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap text-sm font-semibold"
          >
            <i className="ri-calendar-line mr-2"></i>
            Réserver
          </button>
        </div>
      </div>
    </div>
  );
}
